import React, { useEffect, useState } from "react";
import { useUserPermissions } from "../../../UserPermissionsContext";
import { useNavigate } from "react-router";
import Cookies from "js-cookie";
import { all_routes } from "../../../router/all_routes";
import { Select, Spin } from "antd";
import { Link } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import { CloseCircleOutlined } from "@ant-design/icons";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import AudioPlayer from "../audioPlayer";

interface CallDataInterface {
  id: number;
  employee_name: string;
  employee_branch: string;
  agent_name: string;
  call_start_time_: string;
  call_end_time_: string;
  call_duration_hms: string;
  conversation_duration_hms: string;
  caller_status: string;
  destination_status: string;
  call_status: string;
  aws_call_recording_file: string;
  service_division_name: string;
}

const SedAgentCallLogs = () => {
  const { Option } = Select;
  const routes = all_routes;
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState<string>("");
  const [branchDropdown, setBranchDropdown] = useState<any[]>([]);
  const [agentsDropdown, setAgentsDropdown] = useState<any[]>([]);
  const [data, setData] = useState<CallDataInterface[]>([]);
  const [showDateRange, setShowDateRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("sedagentcalllogs");
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
          agent: "",
          branch: "",
          caller_status: "",
          destination_status: "",
          call_status: "",
          employee_name: "",
        };
  });
  useEffect(() => {
    localStorage.setItem("sedagentcalllogs", JSON.stringify(filters));
  }, [filters]);
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject,
    );
  };

  const statusOptions = [
    { label: "Answered", value: "Answered" },
    { label: "Missed", value: "Missed" },
  ];

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  useEffect(() => {
    if (!checkPermission("view_sed_calllogs", "calllog")) {
      navigate(routes.crmLandingPage);
      return;
    }
    fetchData();
  }, [
    userPermissions,
    appliedFilters,
    currentPage,
    searchText,
    pageSize,
    fromDate,
    toDate,
  ]);

  // Branch Dropdown
  const fetchBranches = async () => {
    try {
      const res = await fetch(
        `${webApi}/sed/branches_dropdown_for_calllogs/`,
        options,
      );
      const data = await res.json();
      setBranchDropdown(data);
    } catch (err) {
      console.error(err);
    }
  };

  // Agent DropDown
  const fetchAgents = async () => {
    try {
      const res = await fetch(
        `${webApi}/sed/agents_dropdown_for_calllogs/`,
        options,
      );
      const data = await res.json();
      setAgentsDropdown(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchAgents();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        branch: appliedFilters.branch || "",
        agent: appliedFilters.agent || "",
        employee_name: appliedFilters.employee_name || "",
        caller_status: appliedFilters.caller_status || "",
        destination_status: appliedFilters.destination_status || "",
        call_status: appliedFilters.call_status || "",
        page: String(currentPage),
        search: searchText || "",
      });
      queryParams.append("page_size", String(pageSize));
      const response = await fetch(
        `${webApi}/call/sed_employee_overall_calllogs/?${queryParams.toString()}&from_date=${fromDate}&to_date=${toDate}`,
        options,
      );
      const result = await response.json();
      if (!response.ok) {
        if (result.code === "token_not_valid") {
          navigate(routes.crmLandingPage);
          return;
        }
        throw new Error(result.detail || "Failed to fetch teachers");
      }
      setData(result.results || []);
      setTotalCount(result.total_count || 0);
    } catch (error) {
      console.error("Teachers fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  //Reusable function to display applied filters
  const renderFilterTag = (
    value: string | null,
    filterKey: keyof typeof appliedFilters,
  ) => {
    if (!value) return null;

    return (
      <span
        style={{
          marginLeft: 8,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#1890ff", marginRight: 4 }}>({value})</span>

        <CloseCircleOutlined
          onClick={() => {
            const updatedFilters = {
              ...filters,
              [filterKey]: "",
            };

            setFilters(updatedFilters);
            setAppliedFilters(updatedFilters);
          }}
          style={{ color: "red", cursor: "pointer" }}
        />
      </span>
    );
  };

  const columns = [
    {
      title: (
        <span>
          Employee Name
          {renderFilterTag(
            appliedFilters.employee_name || null,
            "employee_name",
          )}
        </span>
      ),

      dataIndex: "employee_name",
      key: "employee_name",
    },
    {
      title: (
        <span>
          Employee Branch
          {renderFilterTag(
            branchDropdown.find(
              (item) =>
                String(item.branch_id) === String(appliedFilters.branch),
            )?.name || null,
            "branch",
          )}
        </span>
      ),

      dataIndex: "employee_branch",
      key: "employee_branch",
    },

    {
      title: (
        <span>
          Agent Name
          {renderFilterTag(
            agentsDropdown.find(
              (item) => String(item.id) === String(appliedFilters.agent),
            )?.name || null,
            "agent",
          )}
        </span>
      ),

      dataIndex: "agent_name",
      key: "agent_name",
    },

    {
      title: <span>Call Start Time</span>,
      dataIndex: "call_start_time_",
      key: "call_start_time_",
    },

    {
      title: <span>Call End Time</span>,
      dataIndex: "call_end_time_",
      key: "call_end_time_",
    },

    {
      title: <span>Call Duration</span>,
      dataIndex: "call_duration_hms",
      key: "call_duration_hms",
    },

    {
      title: <span>Conversation Duration</span>,
      dataIndex: "conversation_duration_hms",
      key: "conversation_duration_hms",
    },

    {
      title: (
        <span>
          Caller Status
          {renderFilterTag(
            statusOptions.find(
              (item) =>
                String(item.value) === String(appliedFilters.caller_status),
            )?.label || null,
            "caller_status",
          )}
        </span>
      ),

      dataIndex: "caller_status",
      key: "caller_status",
    },

    {
      title: (
        <span>
          Destination Status
          {renderFilterTag(
            statusOptions.find(
              (item) =>
                String(item.value) ===
                String(appliedFilters.destination_status),
            )?.label || null,
            "destination_status",
          )}
        </span>
      ),
      dataIndex: "destination_status",
      key: "destination_status",
    },

    {
      title: (
        <span>
          Call Status
          {renderFilterTag(
            statusOptions.find(
              (item) =>
                String(item.value) === String(appliedFilters.call_status),
            )?.label || null,
            "call_status",
          )}
        </span>
      ),

      dataIndex: "call_status",
      key: "call_status",
    },

    {
      title: <span>Recording</span>,
      render: (
        _text: string,
        record: {
          aws_call_recording_file: string;
          employee_name: string;
          call_start_time_: string;
        },
      ) =>
        record.aws_call_recording_file ? (
          <div style={{ minWidth: "200px" }}>
            <AudioPlayer src={record.aws_call_recording_file} />
          </div>
        ) : (
          <span style={{ color: "#bfbfbf" }}>No recording</span>
        ),
      dataIndex: "aws_call_recording_file",
      key: "aws_call_recording_file",
    },
    // {
    //   title: (
    //     <span>
    //       Service Division
    //       {renderFilterTag(
    //         serviceDivisionOptions.find(
    //           (item) =>
    //             String(item.value) ===
    //             String(appliedFilters.service_division_name),
    //         )?.label || null,
    //         "service_division_name",
    //       )}
    //     </span>
    //   ),

    //   dataIndex: "service_division_name",
    //   key: "service_division_name",
    // },
  ];

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleApplyClick = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setShowFilter(false);
  };
  const handleResetClick = () => {
    const emptyFilters = {
      agent: "",
      caller_status: "",
      destination_status: "",
      call_status: "",
      employee_name: "",
      branch: "",
    };
    localStorage.removeItem("sedagentcalllogs");
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchText("");
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleSelect = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;
    setSelectionRange(ranges.selection);
  };

  const handleApplyDateRange = () => {
    setFromDate(format(selectionRange.startDate, "yyyy-MM-dd"));
    setToDate(format(selectionRange.endDate, "yyyy-MM-dd"));

    setCurrentPage(1);
    setShowDateRange(false);
  };
  const handleResetDateRange = () => {
    setSelectionRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    });

    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setShowDateRange(false);
  };

  const handleExportClick = () => {
    const queryParams = new URLSearchParams({
      branch: appliedFilters.branch || "",
      agent: appliedFilters.agent || "",
      employee_name: appliedFilters.employee_name || "",
      caller_status: appliedFilters.caller_status || "",
      destination_status: appliedFilters.destination_status || "",
      call_status: appliedFilters.call_status || "",
      search: searchText || "",
    });
    let url = `${webApi}/call/export_sed_employee_overall_calllogs/?token=${jwtToken}&${queryParams.toString()}&from_date=${fromDate}&to_date=${toDate}`;
    window.location.href = url;
  };
  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">SED Call Logs</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.SEDDashBoard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Call Logs
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                {checkPermission("export_sed_calllogs", "calllog") && (
                  <button
                    className="btn btn-primary me-2"
                    onClick={handleExportClick}
                  >
                    Export
                  </button>
                )}
              </div>
            </div>
          </div>

          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Call Logs List</h4>

              <div className="d-flex align-items-center gap-2 mb-3 position-relative">
                {/* Date Range Button */}
                <button
                  type="button"
                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                  onClick={() => setShowDateRange(!showDateRange)}
                >
                  <i className="bi bi-calendar3"></i>
                  {fromDate && toDate
                    ? `${fromDate} - ${toDate}`
                    : "Date Range"}
                </button>

                {/* Calendar Popup */}
                {showDateRange && (
                  <div
                    className="position-absolute bg-white shadow rounded border"
                    style={{
                      top: "45px",
                      right: "110px", // adjust if needed
                      zIndex: 999,
                    }}
                  >
                    <DateRange
                      ranges={[selectionRange]}
                      onChange={handleSelect}
                      moveRangeOnFirstSelection={false}
                      editableDateInputs
                      rangeColors={["#0d6efd"]}
                    />

                    <div className="d-flex justify-content-end gap-2 p-3 border-top">
                      <button
                        className="btn btn-outline-danger"
                        onClick={handleResetDateRange}
                      >
                        Reset
                      </button>

                      <button
                        className="btn btn-primary"
                        onClick={handleApplyDateRange}
                      >
                        Apply
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowDateRange(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {/* Filter Button */}
                <button
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setShowFilter(!showFilter);
                    setShowDateRange(false);
                  }}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                </button>
              </div>
            </div>
            {showFilter && (
              <div
                className="card mt-3 shadow-sm"
                style={{
                  width: "500px",
                  position: "absolute",
                  right: 0,
                  zIndex: 1000,
                  borderRadius: "12px",
                }}
              >
                <form>
                  <div className="d-flex align-items-center border-bottom p-3">
                    <h4 className="mb-0 me-auto">Filter</h4>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFilter(false)}
                    />
                  </div>

                  <div className="p-3">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Employee Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={filters.employee_name}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                employee_name: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Agents</label>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select Agent"
                            value={filters.agent}
                            onChange={(value) =>
                              setFilters({
                                ...filters,
                                agent: value,
                              })
                            }
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {agentsDropdown.map((d) => (
                              <Option key={d.id} value={d.id}>
                                {d.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Branch</label>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select Branch"
                            value={filters.branch}
                            onChange={(value) =>
                              setFilters({
                                ...filters,
                                branch: value,
                              })
                            }
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {branchDropdown.map((d) => (
                              <Option key={d.branch_id} value={d.branch_id}>
                                {d.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Caller Status</label>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select Caller Status"
                            value={filters.caller_status || ""}
                            onChange={(value) =>
                              setFilters({
                                ...filters,
                                caller_status: value || "",
                              })
                            }
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {statusOptions.map((teamLead) => (
                              <Option
                                key={teamLead.value}
                                value={teamLead.value}
                              >
                                {teamLead.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Call status</label>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select Call status"
                            value={filters.call_status}
                            onChange={(value) =>
                              setFilters({
                                ...filters,
                                call_status: value,
                              })
                            }
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {statusOptions.map((d) => (
                              <Option key={d.value} value={d.value}>
                                {d.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Destination Status
                          </label>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select Destination Status"
                            value={filters.destination_status || ""}
                            onChange={(value) =>
                              setFilters({
                                ...filters,
                                destination_status: value || "",
                              })
                            }
                            style={{ width: "100%" }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              (option?.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          >
                            {statusOptions.map((teamLead) => (
                              <Option
                                key={teamLead.value}
                                value={teamLead.value}
                              >
                                {teamLead.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 border-top d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={handleResetClick}
                    >
                      Reset
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleApplyClick}
                    >
                      Apply
                    </button>
                  </div>
                </form>
              </div>
            )}
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={data}
                  Selection={false}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: ["10", "25", "100", "500", "1000"],
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                  searchText={searchText}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
    </div>
  );
};

export default SedAgentCallLogs;
